import { PropertySearch } from '__generated__/api';
import { PillProps } from 'components/search/playback/playback-filter-pill';
import * as flatMap from 'flatmap';

export const createSearchAndFilterDisplayOptions = (sortAndFilter: PropertySearch.SortAndFilter): any => {
  const sortAndFilters = sortAndFilter && sortAndFilter.options && sortAndFilter.options.sortAndFilter;

  if (!sortAndFilters) {
    return {
      filters: {
        displayFields: [
          'sort',
          'hotelName',
          'star',
          'price',
          'propertyStyle',
          'paymentType',
          'guestRating',
          'lodging',
          'regionId',
          'poi',
          'amenities',
          'travelerType',
          'deals'
        ]
      }
    };
  }

  return {
    filters: {
      displayFields: sortAndFilters.map(e => e.name)
    }
  };
};

export enum SortAndFilterDisplayField {
  SORT = 'sort',
  HOTEL_NAME = 'hotelName',
  STAR = 'star',
  PRICE = 'price',
  GUEST_RATING = 'guestRating',
  LODGING = 'lodging',
  REGION_ID = 'regionId',
  AMENITIES = 'amenities',
  TRAVELER_TYPE = 'travelerType',
  PAYMENT_TYPE = 'paymentType',
  DEALS = 'deals',
  PROPERTY_STYLE = 'propertyStyle'
}

export const mapSortAndFilter = (propertySearch: PropertySearch.PropertySearch): any => {
  const sortAndFilter: PropertySearch.SortAndFilter | null = propertySearch.sortAndFilter;

  return {
    defaults: getDefaults(sortAndFilter),
    options: getOptions(sortAndFilter),
    pills: getPills(sortAndFilter)
  };
};

export const getSortAndFilterOption = (field: SortAndFilterDisplayField, sortAndFilter: any): PropertySearch._SortAndFilter | null => {
  if (sortAndFilter && sortAndFilter.options) {
    const result = sortAndFilter.options[field];

    return result ? result : null;
  }

  return null;
};

const getDefaults = (sortAndFilter: PropertySearch.SortAndFilter): any => ({
  regionId: sortAndFilter && sortAndFilter.defaults && sortAndFilter.defaults.regionId,
  sort: sortAndFilter && sortAndFilter.defaults && sortAndFilter.defaults.sort.toLowerCase()
});

const getOptions = (sortAndFilter: PropertySearch.SortAndFilter): any => {
  const appliedFilterCount = sortAndFilter && sortAndFilter.options && sortAndFilter.options.appliedFilterCount;
  const sortAndFilterOptions = sortAndFilter && sortAndFilter.options && mapSortAndFilterOptions(sortAndFilter.options.sortAndFilter);

  return Object.assign({}, { appliedFilterCount }, sortAndFilterOptions);
};

const mapSortAndFilterOptions = (sortAndFilterOptions: PropertySearch._SortAndFilter[]): any =>
  Object.assign(
    {},
    ...sortAndFilterOptions.map(sortAndFilterOption => ({
      [sortAndFilterOption['name']]: {
        options: sortAndFilterOption.options.map(mapOption),
        label: sortAndFilterOption.label,
        max: sortAndFilterOption.max,
        min: sortAndFilterOption.min,
        range: sortAndFilterOption.isRange,
        placeholder: sortAndFilterOption.placeholder,
        subLabel: sortAndFilterOption.subLabel,
        step: sortAndFilterOption.step,
        name: sortAndFilterOption.name,
        hiddenThreshold: sortAndFilterOption.hiddenThreshold
      }
    }))
  );

const mapOption = (option: PropertySearch._Options): any => ({
  selected: option.isSelected,
  ...option,
  value: option.optionValue
});

const getPills = (filters: PropertySearch.SortAndFilter): PillProps[] => {
  const selectedFilters = filters.options.sortAndFilter.filter(filter => filter.selected);

  return flatMap(selectedFilters, (filter: PropertySearch._SortAndFilter) => getPillsFromFilter(filter, filters.selected.priceFilterPills));
};

const getPillsFromFilter = (filter: PropertySearch._SortAndFilter, pricePills: PropertySearch.PriceFilterPills): PillProps[] => {
  if (filter.name === 'price') {
    return [getPriceFilterPill(pricePills)];
  } else {
    return filter.options.filter(option => option.isSelected).map(option => getPill(filter, option));
  }
};

const getPill = (filter: PropertySearch._SortAndFilter, option: PropertySearch._Options): PillProps => ({
  query: '',
  name: filter.name,
  label: option.selectedLabel,
  value: option.optionValue,
  generated: true
});

const getPriceFilterPill = (pricePills: PropertySearch.PriceFilterPills): PillProps => ({
  query: '',
  name: 'price',
  label: pricePills && pricePills.aggregate.label,
  value: pricePills && pricePills.aggregate.value,
  generated: true
});
